import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { ChannelPageHero, ChannelEmpty } from '@/pages/WatchPage';
import { useGlobalAudio } from '@/components/GlobalAudioPlayer';
import { Play, Pause, Music as MusicIcon, Newspaper, ShoppingBag, Video as VideoIcon, Image as ImageIcon, ExternalLink } from 'lucide-react';

/**
 * DynamicChannelPage
 * ------------------
 * Renders the public page for any user-created channel.
 * - Reads channelKey from URL params.
 * - Fetches /api/channels to confirm the channel exists (and grab metadata).
 * - Loads content across content types (audio / articles / videos / products / gallery)
 *   and filters by channel_key === channelKey.
 * - Falls back to a NotFound-style empty view if the channel is missing.
 *
 * Channels organize content. Content types determine behavior. Products may
 * appear on any channel — matching the Universal Content Creation spec.
 */
export default function DynamicChannelPage() {
  const { channelKey } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'not_found'
  const [channel, setChannel] = useState(null);
  const [content, setContent] = useState({ audio: [], articles: [], videos: [], products: [], gallery: [] });
  const { current, isPlaying, togglePlay, setQueueAndPlay } = useGlobalAudio();

  // Reserved routes that map to dedicated pages — the dynamic route
  // must NOT hijack these. React Router handles literal routes first, so
  // this list is defensive only (for direct-nav edge cases).
  const RESERVED = useMemo(
    () => new Set([
      'watch', 'music', 'podcasts', 'on-my-mind', 'meet-g', 'collab',
      'legal', 'checkout', 'vault', 'collect', 'admin', 'listen', 'read', 'cinema',
      'record-room', 'observation-room', 'gallery', 'greenhouse',
    ]),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setStatus('loading');
      if (!channelKey || RESERVED.has(channelKey)) {
        setStatus('not_found');
        return;
      }
      try {
        // Fetch channels to verify existence
        const { data: channels } = await api.get('/channels');
        const found = (channels || []).find((c) => c.key === channelKey || c.route === `/${channelKey}`);
        if (cancelled) return;
        if (!found) {
          setStatus('not_found');
          return;
        }
        setChannel(found);
        // Fetch all content types in parallel; each endpoint is public + safe.
        // For articles, pass channel_key so channel-assigned articles are
        // returned (they are excluded from the default /articles response).
        const [audio, articles, videos, products, gallery] = await Promise.all([
          api.get('/audio').then((r) => r.data).catch(() => []),
          api.get(`/articles?channel_key=${encodeURIComponent(found.key)}`).then((r) => r.data).catch(() => []),
          api.get('/videos').then((r) => r.data).catch(() => []),
          api.get('/products').then((r) => r.data).catch(() => []),
          api.get('/gallery').then((r) => r.data).catch(() => []),
        ]);
        if (cancelled) return;
        // Filter by the RESOLVED channel key (from the found object), not the URL slug.
        // The URL slug may be the route (e.g. "only-on-planet-g") while stored
        // channel_key uses the canonical key (e.g. "only_on_planet_g").
        const resolvedKey = found.key;
        const byKey = (arr) => (arr || []).filter((it) => it && it.channel_key === resolvedKey);
        setContent({
          audio: byKey(audio),
          articles: byKey(articles),
          videos: byKey(videos),
          products: byKey(products),
          gallery: byKey(gallery),
        });
        setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('not_found');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [channelKey, RESERVED]);

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="dynamic-channel-loading">
        <div className="pg-card p-8 text-center text-[var(--pg-ink-muted)]">Tuning in…</div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="dynamic-channel-not-found">
        <div className="pg-card p-10 text-center max-w-md">
          <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-pink">404 — OFF AIR</p>
          <h1 className="font-display text-3xl mt-3 tracking-[0.04em]">THIS CHANNEL DOESN&apos;T EXIST.</h1>
          <p className="text-[var(--pg-ink-muted)] mt-3">Tune back to the main signal.</p>
          <Link to="/" className="mt-6 inline-block pg-btn-neon">Return home</Link>
        </div>
      </div>
    );
  }

  const kicker = `CHANNEL — ${(channel.label || channel.key || '').toUpperCase()}`;
  const title = (channel.label || channel.key || '').toUpperCase();
  const blurb = channel.description || 'Signal detected. Content shifts as items are added.';
  const accent = channel.accent || 'emerald';
  const totalItems = content.audio.length + content.articles.length + content.videos.length + content.products.length + content.gallery.length;

  // Reusable Audio Experience — when the channel is configured as an audio
  // (or podcast) experience, render the streaming-style card grid + Play All.
  // Also auto-detect a podcast surface when either (a) the channel key
  // contains "podcast" (case-insensitive) or (b) every audio item on the
  // channel is of kind === 'podcast'. This ensures user-created podcast
  // channels render with cover art and episode cards even if their
  // content_experience is still the default.
  const experience = (channel.content_experience || 'standard').toLowerCase();
  const keyContainsPodcast = ((channel.key || '') + ' ' + (channel.route || '')).toLowerCase().includes('podcast');
  const allAudioArePodcast = content.audio.length > 0 && content.audio.every((a) => (a && a.kind) === 'podcast');
  const shouldUseAudioExperience =
    (experience === 'audio' || experience === 'podcast' || keyContainsPodcast || allAudioArePodcast) &&
    content.audio.length > 0;
  if (shouldUseAudioExperience) {
    const experienceKind = (experience === 'audio' || experience === 'podcast')
      ? experience
      : (keyContainsPodcast || allAudioArePodcast) ? 'podcast' : 'audio';
    return (
      <div data-testid="dynamic-channel-page" data-experience={experienceKind}>
        <ChannelPageHero kicker={kicker} title={title} blurb={blurb} accent={accent} />
        <ChannelAudioExperience items={content.audio} kind={experienceKind} channelLabel={channel.label} />
      </div>
    );
  }

  return (
    <div data-testid="dynamic-channel-page" data-experience={experience}>
      <ChannelPageHero kicker={kicker} title={title} blurb={blurb} accent={accent} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-32 space-y-10">
        {totalItems === 0 ? (
          <ChannelEmpty
            title="THIS FREQUENCY IS QUIET"
            copy="No content assigned to this channel yet. Use the admin dashboard's Add Content action to place items here."
          />
        ) : (
          <>
            {content.audio.length > 0 && (
              <AudioSection items={content.audio} current={current} isPlaying={isPlaying} togglePlay={togglePlay} setQueueAndPlay={setQueueAndPlay} />
            )}
            {content.videos.length > 0 && <VideoSection items={content.videos} />}
            {content.articles.length > 0 && <ArticleSection items={content.articles} />}
            {content.products.length > 0 && <ProductSection items={content.products} />}
            {content.gallery.length > 0 && <GallerySection items={content.gallery} />}
          </>
        )}
      </section>
    </div>
  );
}

/* ---------------- Reusable Audio Experience (Phase 19) ---------------- */

function ChannelAudioExperience({ items, kind, channelLabel }) {
  const {
    current, isPlaying, togglePlay, playTrack, playAll,
    shuffle, setShuffle, playlistMode,
  } = useGlobalAudio();
  const playable = items.filter((it) => it.file_url || (it.source_type === 'embed' && it.embed_url));
  const handlePlay = (t) => (current?.id === t.id ? togglePlay() : playTrack(t));
  const handlePlayAll = () => playable.length && playAll(playable);
  const handleShuffleAll = () => {
    if (!playable.length) return;
    if (!shuffle) setShuffle(true);
    playAll(playable);
  };
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-32">
      <div className="pg-card p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="channel-audio-header">
        <div className="min-w-0">
          <p className="pg-label text-[10px]">{(channelLabel || 'AUDIO').toUpperCase()} LIBRARY</p>
          <p className="text-[11px] font-mono tracking-widest text-[var(--pg-ink-faint)] mt-1">
            {playable.length} TRACK{playable.length === 1 ? '' : 'S'}{playlistMode ? ' · PLAYLIST MODE' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleShuffleAll} data-testid="channel-audio-shuffle-all" className="pg-btn-ghost rounded-[var(--pg-radius-md)] px-3 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2">
            <span aria-hidden>🔀</span> SHUFFLE ALL
          </button>
          <button onClick={handlePlayAll} data-testid="channel-audio-play-all" className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2">
            <span aria-hidden>▶</span> PLAY ALL
          </button>
        </div>
      </div>
      <div className={kind === 'podcast' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5'} data-testid="channel-audio-grid">
        {items.map((it) => {
          const active = current?.id === it.id;
          const showPlay = !active || !isPlaying;
          const disabled = !(it.file_url || (it.source_type === 'embed' && it.embed_url));
          return (
            <div key={it.id} className={`group pg-card overflow-hidden transition-colors ${active ? 'border-[var(--pg-emerald)]' : 'hover:border-[rgba(25,194,142,0.4)]'}`} data-testid="channel-audio-card" data-active={active ? '1' : '0'}>
              <div className={kind === 'podcast' ? 'flex items-start gap-4 p-4' : ''}>
                <div className={kind === 'podcast' ? 'relative h-24 w-24 sm:h-28 sm:w-28 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]' : 'relative aspect-square bg-[var(--pg-navy-800)] overflow-hidden'}>
                  {it.cover_url ? <img src={mediaUrl(it.cover_url)} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)] text-2xl">♪</div>}
                  <button
                    onClick={() => !disabled && handlePlay(it)}
                    disabled={disabled}
                    data-testid="channel-audio-card-play"
                    aria-label={showPlay ? `Play ${it.title}` : `Pause ${it.title}`}
                    className={`absolute inset-0 flex items-center justify-center transition-opacity ${disabled ? 'opacity-40 cursor-not-allowed' : active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    style={{ background: 'rgba(4,6,12,0.4)' }}
                  >
                    <span className={`${kind === 'podcast' ? 'h-11 w-11' : 'h-14 w-14'} rounded-full flex items-center justify-center ${disabled ? 'bg-[var(--pg-navy-800)] text-[var(--pg-ink-faint)]' : 'bg-[var(--pg-emerald)] text-[var(--pg-ink)]'}`}>
                      {showPlay ? <Play size={kind === 'podcast' ? 16 : 20} className="ml-0.5" /> : <Pause size={kind === 'podcast' ? 16 : 20} />}
                    </span>
                  </button>
                </div>
                <div className={kind === 'podcast' ? 'min-w-0 flex-1' : 'p-3 sm:p-4'}>
                  <p className={`font-serif text-sm sm:text-base line-clamp-2 leading-snug ${active ? 'pg-text-glow-emerald' : 'text-[var(--pg-ink)]'}`}>{it.title}</p>
                  <p className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase mt-1 truncate">
                    {it.kind || kind}{it.duration ? ` · ${it.duration}` : ''}
                  </p>
                  {kind === 'podcast' && it.description && <p className="text-xs text-[var(--pg-ink-muted)] mt-2 line-clamp-3">{it.description}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Sections ---------------- */

function SectionHeader({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-[var(--pg-border-2)] bg-black/30 text-[var(--pg-emerald-2)]">
        <Icon size={16} />
      </span>
      <div>
        <p className="pg-label text-[10px]">{label}</p>
        <p className="font-mono text-[11px] text-[var(--pg-ink-faint)] tracking-widest">{count} {count === 1 ? 'ITEM' : 'ITEMS'}</p>
      </div>
    </div>
  );
}

function AudioSection({ items, current, isPlaying, togglePlay, setQueueAndPlay }) {
  const playable = items.filter((it) => it.file_url || (it.source_type === 'embed' && it.embed_url));
  const handlePlay = (track) => {
    if (current?.id === track.id) { togglePlay(); return; }
    const idx = playable.findIndex((t) => t.id === track.id);
    setQueueAndPlay(playable, idx >= 0 ? idx : 0);
  };
  return (
    <div data-testid="dc-audio-section">
      <SectionHeader icon={MusicIcon} label="AUDIO" count={items.length} />
      <div className="pg-card overflow-hidden">
        <ul className="divide-y divide-[var(--pg-border-2)]">
          {items.map((it, i) => {
            const active = current?.id === it.id;
            const showPlay = !active || !isPlaying;
            const disabled = !(it.file_url || (it.source_type === 'embed' && it.embed_url));
            return (
              <li key={it.id} className={`px-5 py-3 flex items-center gap-4 transition-colors ${active ? 'bg-[rgba(25,194,142,0.06)]' : 'hover:bg-[rgba(242,238,227,0.04)]'}`} data-testid="dc-audio-row">
                <button
                  onClick={() => !disabled && handlePlay(it)}
                  disabled={disabled}
                  className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${disabled ? 'bg-[var(--pg-navy-800)] text-[var(--pg-ink-faint)] cursor-not-allowed' : 'bg-[var(--pg-emerald)] hover:bg-[var(--pg-emerald-2)] text-[var(--pg-ink)]'}`}
                  aria-label={showPlay ? `Play ${it.title}` : `Pause ${it.title}`}
                  data-testid="dc-track-play"
                >
                  {showPlay ? <Play size={14} className="ml-0.5" /> : <Pause size={14} />}
                </button>
                <span className="font-mono text-[11px] text-[var(--pg-ink-faint)] w-6 shrink-0 hidden sm:block">{String(i + 1).padStart(2, '0')}</span>
                <div className="h-10 w-10 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)] hidden sm:block">
                  {it.cover_url ? <img src={mediaUrl(it.cover_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><MusicIcon size={14} /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`font-serif text-base truncate ${active ? 'pg-text-glow-emerald' : 'text-[var(--pg-ink)]'}`}>{it.title}</p>
                  <p className="text-xs text-[var(--pg-ink-muted)] mt-0.5 truncate">{it.kind}{it.duration ? ` · ${it.duration}` : ''}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function VideoSection({ items }) {
  return (
    <div data-testid="dc-video-section">
      <SectionHeader icon={VideoIcon} label="WATCH" count={items.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => (
          <a key={it.id} href={it.embed_url} target="_blank" rel="noreferrer" className="pg-card overflow-hidden group" data-testid="dc-video-card">
            <div className="aspect-video bg-[var(--pg-navy-800)] relative">
              {it.poster_url ? <img src={mediaUrl(it.poster_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><VideoIcon size={32} /></div>}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="h-12 w-12 rounded-full bg-[var(--pg-emerald)]/90 flex items-center justify-center"><Play size={16} className="ml-1 text-[var(--pg-ink)]" /></span>
              </div>
            </div>
            <div className="p-4">
              <p className="font-serif text-base text-[var(--pg-ink)] line-clamp-2">{it.title}</p>
              {it.description && <p className="text-xs text-[var(--pg-ink-muted)] mt-1 line-clamp-2">{it.description}</p>}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function ArticleSection({ items }) {
  return (
    <div data-testid="dc-article-section">
      <SectionHeader icon={Newspaper} label="READING" count={items.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => {
          const isExternal = it.content_type === 'external_link' && it.external_url;
          const href = isExternal ? it.external_url : `/on-my-mind/${it.slug || it.id}`;
          const target = isExternal ? '_blank' : undefined;
          const rel = isExternal ? 'noreferrer' : undefined;
          return (
            <a
              key={it.id}
              href={href}
              target={target}
              rel={rel}
              className="pg-card overflow-hidden group flex flex-col sm:flex-row"
              data-testid={isExternal ? 'dc-external-link-card' : 'dc-article-card'}
            >
              {it.cover_url && (
                <div className="sm:w-40 aspect-video sm:aspect-auto bg-[var(--pg-navy-800)] shrink-0">
                  <img src={mediaUrl(it.cover_url)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-serif text-base text-[var(--pg-ink)] line-clamp-2 flex-1">{it.title}</p>
                  {isExternal && <ExternalLink size={13} className="text-[var(--pg-ink-faint)] shrink-0" />}
                </div>
                {(it.excerpt || it.subtitle) && <p className="text-xs text-[var(--pg-ink-muted)] mt-1 line-clamp-2">{it.excerpt || it.subtitle}</p>}
                <p className="text-[10px] font-mono tracking-widest uppercase text-[var(--pg-ink-faint)] mt-2">{it.read_time || (isExternal ? 'EXTERNAL LINK' : 'ARTICLE')}</p>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function ProductSection({ items }) {
  return (
    <div data-testid="dc-product-section">
      <SectionHeader icon={ShoppingBag} label="COMMERCE" count={items.length} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <Link key={it.id} to={`/vault/${it.id}`} className="pg-card overflow-hidden group" data-testid="dc-product-card">
            <div className="aspect-square bg-[var(--pg-navy-800)]">
              {it.cover_url ? <img src={mediaUrl(it.cover_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-gold-2)]"><ShoppingBag size={28} /></div>}
            </div>
            <div className="p-3">
              <p className="font-serif text-sm text-[var(--pg-ink)] line-clamp-2">{it.title}</p>
              <p className="text-xs text-[var(--pg-gold-2)] font-mono mt-1">${(it.price ?? 0).toFixed?.(2) ?? it.price} {(it.currency || 'usd').toUpperCase()}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function GallerySection({ items }) {
  return (
    <div data-testid="dc-gallery-section">
      <SectionHeader icon={ImageIcon} label="GALLERY" count={items.length} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.id} className="pg-card overflow-hidden" data-testid="dc-gallery-card">
            <div className="aspect-square bg-[var(--pg-navy-800)]">
              {it.image_url && <img src={mediaUrl(it.image_url)} alt={it.title || ''} className="w-full h-full object-cover" />}
            </div>
            {(it.title || it.caption) && (
              <div className="p-2">
                {it.title && <p className="text-xs text-[var(--pg-ink)] truncate">{it.title}</p>}
                {it.caption && <p className="text-[10px] text-[var(--pg-ink-muted)] truncate">{it.caption}</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
